<template>
  <div class="app-centered-form">
    <app-loading :visible="loading" />
    <form class="md-layout from-box" v-on:keyup.enter="updateMember">
      <md-card class="md-layout-item md-size-250 md-small-size-250">
        <member-form-fields v-model="member"
          :error-message="validationError"
          :selectOrg="organization"
          v-on:resetOrg="resetOrg"
          v-on:orgSelected="orgSelected"
          ref="formFields" />
        <md-card-actions>
          <md-button class="md-raised md-primary save-placing"
            v-on:click="updateMember" v-lang.save v-if="canManage">
          </md-button>
            <md-dialog-confirm
              :md-active.sync="active"
              :md-title="translate('textForDeleteMember')"
              :md-confirm-text="translate('yes')"
              :md-cancel-text="translate('close')"
              @md-confirm="onDeleteConfirm" />
          <md-button class="md-raised md-accent delete-placing"
            @click="active = true"
            v-lang.delete
            v-if="canManage">
          </md-button>
          <md-button class="md-raised"
            :to="{ name: 'MemberListPage', query: getBackQuery() }">
            <span v-lang.back></span>
          </md-button>
        </md-card-actions>
      </md-card>
    </form>
  </div>
</template>

<script>
import MemberService from '@/common/services/member'
import OrganizationService from '@/common/services/organization'
import MemberFormFields from './FormFields.vue'
import AppLoading from '@/components/ui/AppLoading.vue'
import HasLoading from '@/mixins/HasLoading.mixin'
import Auth from '@//common/auth/index.js'
import ValidateMixin from '@/components/members/Validate.mixin'
import parseBadRequest from '@/utils/parse-bad-request'
import testForErrorCode from '@/utils/test-for-err-code'
import ERRCODES, { isUnknownError } from '@/utils/error-codes'

export default {
  name: 'MemberEditForm',
  components: {
    MemberFormFields,
    AppLoading
  },
  mixins: [
    HasLoading,
    ValidateMixin
  ],
  data () {
    return {
      active: false,
      value: null,
      /** @type {Member} */
      member: {
        partyOrganizationId: '',
        cardNumber: '',
        firstName: '',
        lastName: '',
        patronymic: '',
        mobilePhoneNumber: ''
      },
      validationError: '',
      canManage: Auth.CanManageMembers(),
      /** @type {Organization} */
      organization: {}
    }
  },
  watch: {
    member: {
      deep: true,
      handler () {
        this.hideErrors()
      }
    }
  },
  created () {
    this.readMember()
  },
  methods: {
    getBackQuery () {
      const assignee = {}
      if (this.$route.query.party_organization_id) {
        assignee.party_organization_id = this.member.party_organization_id
      }

      return Object.assign(assignee, this.$route.query)
    },
    readMember () {
      this.startAppLoading()
      MemberService.Get(this.$route.params.memberId)
        .then(response => {
          this.member = response.data
          this.readOrganization()
        })
        .catch(error => {
          this.$httpError.handle.call(this, error)
        })
        .then(() => {
          this.finishAppLoading()
        })
    },
    readOrganization () {
      this.startAppLoading()

      OrganizationService.Get(this.member.partyOrganizationId)
        .then(response => {
          this.organization = response.data
        })
        .catch(error => {
          this.$httpError.handle.call(this, error)
        })
        .then(() => {
          this.finishAppLoading()
        })
    },
    processErrorsWithNotice () {
      const errorMessages = parseBadRequest(this.validationError)
      if (errorMessages.length > 0) {
        let hasUnknowError = false
        errorMessages.forEach(errorMsg => {
          if (testForErrorCode(ERRCODES.members.rootMember, errorMsg)) {
            this.$error(this.translate('error_2_1'))
          } else if (isUnknownError(errorMsg, 'members')) {
            hasUnknowError = true
          }
        })

        if (hasUnknowError) {
          this.$error(this.translate('unexpectedError'), 5000)
        }
      }
    },
    updateMember () {
      const validationErrors = this.validate(this.member)

      if (validationErrors.length === 0) {
        this.startAppLoading()

        MemberService.Put(this.member)
          .then(() => {
            this.$success(this.translate('successfulEditMember'), 5000)
            this.updateMemberBack()
          })
          .catch(error => {
            if (error.response.status === 400) {
              this.validationError = error.response.data.message
              this.processErrorsWithNotice()
            } else {
              this.$httpError.handle.call(this, error)
            }
          })
          .then(() => {
            this.finishAppLoading()
          })
      } else {
        this.validationError = this.$errorsToString(validationErrors)
      }
    },
    deleteMember () {
      this.startAppLoading()

      MemberService.Delete(this.member)
        .then(() => {
          this.$router.push(
            {
              name: 'MemberListPage',
              query: this.getBackQuery()
            },
            () => { this.$success(this.translate('textAfterDeleteMember'), 5000) }
          )
        })
        .catch(error => {
          if (error.response.status === 400) {
            const errorForDelete = error.response.data.message
            const errorMessages = parseBadRequest(errorForDelete)
            if (errorMessages.length > 0) {
              errorMessages.forEach(errorMsg => {
                if (testForErrorCode('error:2.1', errorMsg)) {
                  this.$error(this.translate('error_2_1'), 5000)
                } else {
                  this.$error(this.translate('unexpectedError'), 5000)
                }
              })
            }
          }
        })
        .then(() => {
          this.finishAppLoading()
        })
    },
    onDeleteConfirm () {
      this.deleteMember()
    },
    hideErrors () {
      this.validationError = ''
    },
    resetOrg () {
      this.member.partyOrganizationId = ''
    },
    updateMemberBack () {
      this.$router.push({
        name: 'MemberListPage',
        query: this.getBackQuery()
      })
    },
    orgSelected (org) {
      this.member.partyOrganizationId = org.id
    }
  }
}
</script>

<style lang="scss" scoped>
.md-title {
  display: flex;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/components/members/EditForm.vue